import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AppLayout from "../components/layout/AppLayout";
import BreadcrumbNav from "../components/layout/BreadcrumbNav";
import type { InkjetPrinter } from "../types";
import { apiClient } from "../utils/api";
import { useWebSocket } from "../utils/useWebSocket";

interface PrinterRealtimeEvent {
  type: string;
  data?: any;
  timestamp: number;
}

const InkjetPrintersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<InkjetPrinter | null>(null);
  const [realtimeEvents, setRealtimeEvents] = useState<
    Map<string, PrinterRealtimeEvent>
  >(new Map());
  const [form] = Form.useForm();

  const {
    data: printers = [],
    isLoading,
    refetch,
  } = useQuery<InkjetPrinter[]>({
    queryKey: ["inkjetPrinters"],
    queryFn: () => apiClient.getInkjetPrinters(),
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<InkjetPrinter>) =>
      apiClient.createInkjetPrinter(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inkjetPrinters"] });
      message.success("Created successfully");
    },
    onError: () => {
      message.error("Failed to save");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InkjetPrinter> }) =>
      apiClient.updateInkjetPrinter(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inkjetPrinters"] });
      message.success("Updated successfully");
    },
    onError: () => {
      message.error("Failed to save");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteInkjetPrinter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inkjetPrinters"] });
      message.success("Deleted successfully");
    },
    onError: () => {
      message.error("Failed to delete");
    },
  });

  const { connected, connectionStatus, subscribe } = useWebSocket(undefined, {
    deviceType: 'inkjet-printer',
    onMessage: (msg) => {
      if (
        (msg.type === "print_job" || msg.type === "device_command") &&
        msg.device_id
      ) {
        setRealtimeEvents((prev) => {
          const newMap = new Map(prev);
          newMap.set(msg.device_id!, {
            type: msg.type,
            data: msg.data,
            timestamp: msg.timestamp ?? Date.now() / 1000,
          });
          return newMap;
        });
      }
    },
    onConnect: () => {
      message.success("Printer real-time updates connected");
    },
  });

  useEffect(() => {
    if (connected && printers.length > 0) {
      const deviceIds = printers.map((p) => p.device_id);
      subscribe(deviceIds);
    }
  }, [connected, printers, subscribe]);

  const handleEdit = (item: InkjetPrinter) => {
    setEditingItem(item);
    form.setFieldsValue(item);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
      setModalVisible(false);
    } catch (error) {
      console.error("Failed to save:", error);
    }
  };

  const columns: ColumnsType<InkjetPrinter> = [
    {
      title: "Device ID",
      dataIndex: "device_id",
      key: "device_id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={status === "online" ? "green" : "red"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
    },
    {
      title: "Last Event",
      key: "last_event",
      render: (_, record) => {
        const event = realtimeEvents.get(record.device_id);
        if (!event) return <span style={{ color: '#999' }}>-</span>;
        const isStale = Date.now() / 1000 - event.timestamp > 10;
        const label = event.type === "print_job" ? "Print Job" : "Command";
        return (
          <Space>
            <Tag color={isStale ? "default" : "green"}>{label}</Tag>
            {!isStale && <Tag color="blue">LIVE</Tag>}
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this printer?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout
      title="Inkjet Printers Management"
      extraActions={
        <Space>
          <Tooltip title={`Real-time updates: ${connectionStatus}`}>
            <div className="flex items-center gap-4">
              <Badge
                status={
                  connectionStatus === "connected"
                    ? "processing"
                    : connectionStatus === "connecting"
                    ? "warning"
                    : "error"
                }
              />{" "}
              {connectionStatus === "connected"
                ? "Live"
                : connectionStatus === "connecting"
                ? "Connecting..."
                : "Offline"}
            </div>
          </Tooltip>
          <Tooltip title="Refresh data">
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              loading={isLoading}
            />
          </Tooltip>
        </Space>
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <BreadcrumbNav />
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Printer
        </Button>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={printers}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 700 }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edit Printer" : "Add Printer"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="device_id"
            label="Device ID"
            rules={[{ required: true, message: "Please enter device ID" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="offline">
            <Input />
          </Form.Item>
          <Form.Item name="ip_address" label="IP Address">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </AppLayout>
  );
};

export default InkjetPrintersPage;
